import { LoadingIndicator, SiteFooter, ToastProvider } from '@laerdal/life-react-components';
import { Component, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Route, RouterProvider, useLocation, useNavigate, useParams } from 'react-router';
import styled from 'styled-components';
import i18next from 'i18next';
import { initReactI18next, I18nextProvider, useTranslation } from 'react-i18next';
import ArticlePage from './pages/articlePage/ArticlePage';
import ArticleSearchPage from './pages/articleSearchPage/ArticleSearchPage';
import LandingPage from './pages/landingPage/LandingPage';
import EmbededSearchPage from './pages/embededSearchPage/EmbededSearchPage';
import EmbededArticlePage from './pages/embededArticlePage/EmbededArticlePage';
import { createBrowserRouter, Routes, ScrollRestoration } from 'react-router-dom';
import Header from './commonComponents/Header';
import posthog from 'posthog-js';
import CaseCreatePage from './pages/cases/CaseCreatePage';
import PartFinderPage from './pages/partFinderPage/PartFinderSearchPage';
import PartFinderSearchPage from './pages/partFinderPage/PartFinderSearchPage';
import PartFinderItemPage from './pages/partFinderPage/PartFinderItemPage';
import ProductCategoryPage from './pages/landingPage/ProductCategoryPage';
import ProductLinePage from './pages/landingPage/ProductLinePage';
import ProductLineComponent from './pages/landingPage/ProductLineComponent';
import CaseSuboptionsPage from './pages/cases/CaseSuboptionsPage';
import CaseFormPage from './pages/cases/CaseFormPage';
import CaseConfirmationPage from './pages/cases/CaseConfirmationPage';
import ProductLineArticlesPage from './pages/landingPage/ProductLineArticlesPage';
import backend from "i18next-http-backend";
import React from 'react';
import LanguageSelector from './commonComponents/Language';
import { useDispatch, useSelector } from 'react-redux';
import { setMarket, setLocale, selectMarket, selectLocale, setTranslateWithAi, setTranslateWithAiMarket } from './store/slices/globalConfigSlice';
import { selectUserProfile, setGigyaAccount, setUserProfile } from './store/slices/userSlice';
import UserApi from './services/api/UserApi';
import AuthApi from './services/api/AuthApi';
import SimcapturePage from './pages/simcapture/simcapturePage';
import { User } from './model/dto/userProfile/user';
import { Industry } from './model/dto/userProfile/industry';

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const FooterContainer = styled.div`
  margin-top: auto;
`;

const TestIframe = styled.iframe`
  height: 95vh;
`;

const LoadingContainer = styled.div`
  padding: 50px;
`;


// Initialize i18next
i18next
  .use(backend)
  .use(initReactI18next)
  .init({
    lng: 'en',
    debug: false,
    load: 'currentOnly',
    cleanCode: true,
    keySeparator: false,
    fallbackLng: 'en',
    compatibilityJSON: 'v3',
    defaultNS: 'Common',
    interpolation: {
      escapeValue: false,
      format: function (value: any, format: any, lng: any) {
        // if (value instanceof Date) {
        //   return moment(value).format(format);
        // }
        return value;
      },
    },
    react: {
      useSuspense: true,
    },
    nonExplicitSupportedLngs: false,
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json'
    }
  });

const App = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const currentLocale = useSelector(selectLocale);
  const currentMartket = useSelector(selectMarket);
  const navigate = useNavigate();
  const { t } = useTranslation(['translation', 'cases', 'landingPage']);
  const [loadingApp, setLoadingApp] = useState<boolean>(true);
  const [showHeaderFooter, setShowHeaderFooter] = useState<boolean>(true);
  const [initialMarketSet, setInitialMarketSet] = useState<boolean>(false);
  const user = useSelector(selectUserProfile);

  useEffect(() => {
    i18next.changeLanguage(currentLocale);
  }, [currentLocale]);

  window.addEventListener("message",
    (event) => {
      if (event.data == 'closingHelpU') {
        // @ts-ignore
        posthog?.capture('helpU closed', {
          $geoip_disable: true,
        });
      }
    },
    false
  );

  const handlePageChange = () => {
    // @ts-ignore
    let timeSpent = Math.floor((Date.now() - window.supportStartTime) / 1000);
    posthog.capture('SUPP-PageLeave',
      {
        seconds_on_page: timeSpent,
        //@ts-ignore
        url: window.supportFullUrl
      }
    );


    // @ts-ignore
    window.supportStartTime = new Date().getTime();
    // @ts-ignore
    window.supportFullUrl = window.location.href;
  };

  const checkMarket = (market: string) => {
    return location.pathname.toLowerCase() == market || location.pathname.toLowerCase().startsWith(`${market}/`);
  };


  const onLoginHandler = (eventObj: any) => {
    dispatch(
      setGigyaAccount({
        country: eventObj.data?.organization?.country,
        dateRegistered: eventObj.registered,
        email: eventObj.profile.email,
        firstName: eventObj.profile.firstName,
        identityId: eventObj.UID,
        lastLogin: eventObj.lastLogin,
        lastName: eventObj.profile.lastName,
        locale: eventObj.profile.locale ?? 'en',
        market: eventObj.data.commerce?.market,
      }),
    );

    return UserApi.getUser()
      .then((user) => {
        dispatch(setUserProfile(user));
      })
  };

  function onLogoutHandler() {
    //@ts-ignore
    window.posthog.reset?.();
    window.location.reload?.();
  }

  useEffect(() => {
    //@ts-ignore
    window.gigya.accounts.addEventHandlers({
      onLogout: onLogoutHandler,
      onLogin: onLoginHandler
    });

    //SSO
    AuthApi.retrieveAndStoreJWT()
      .then(check => check
        && AuthApi.retrieveAccountInfo()
          .then(onLoginHandler))
      .finally(() => {
        setLoadingApp(false)
      }
      )
  }, []);

  //Market redirection
  useEffect(() => {
    const userMarket = user?.currentOrganization?.services?.find(x => !!x.commerceMarket)?.commerceMarket?.code?.toLocaleLowerCase() ?? '';
    if (initialMarketSet
      && !!user
      && (userMarket != currentMartket)) {

      //return;
      let pathWithoutMarket = location.pathname;

      if (!!userMarket && userMarket != currentMartket) {
        dispatch(setMarket(userMarket));

        if (pathWithoutMarket.startsWith(`/${currentMartket}`))
          pathWithoutMarket = location.pathname.substring(currentMartket.length + 1);

        if (pathWithoutMarket && !pathWithoutMarket.startsWith('/')) pathWithoutMarket = `/${pathWithoutMarket}`;

        navigate(`/${userMarket}${pathWithoutMarket}${location.search}`);
      }
    }
  }, [initialMarketSet, currentMartket, user]);


  useEffect(() => {
    let marketToSet = ''
    if (checkMarket('/la'))
      marketToSet = 'la';
    else if (checkMarket('/br'))
      marketToSet = 'br';
    else if (checkMarket('/ca'))
      marketToSet = 'ca';
    else if (checkMarket('/mx'))
      marketToSet = 'mx';
    else if (checkMarket('/us'))
      marketToSet = 'us';
    else if (checkMarket('/au'))
      marketToSet = 'au';
    else if (checkMarket('/hk'))
      marketToSet = 'hk';
    else if (checkMarket('/in'))
      marketToSet = 'in';
    else if (checkMarket('/jp'))
      marketToSet = 'jp';
    else if (checkMarket('/kr'))
      marketToSet = 'kr';
    else if (checkMarket('/my'))
      marketToSet = 'my';
    else if (checkMarket('/nz'))
      marketToSet = 'nz';
    else if (checkMarket('/sg'))
      marketToSet = 'sg';
    else if (checkMarket('/be-nl'))
      marketToSet = 'be-nl';
    else if (checkMarket('/be-fr'))
      marketToSet = 'be-fr';
    else if (checkMarket('/dk'))
      marketToSet = 'dk';
    else if (checkMarket('/de'))
      marketToSet = 'de';
    else if (checkMarket('/es'))
      marketToSet = 'es';
    else if (checkMarket('/fr'))
      marketToSet = 'fr';
    else if (checkMarket('/it'))
      marketToSet = 'it';
    else if (checkMarket('/nl'))
      marketToSet = 'nl';
    else if (checkMarket('/no'))
      marketToSet = 'no';
    else if (checkMarket('/pl'))
      marketToSet = 'pl';
    else if (checkMarket('/cn'))
      marketToSet = 'cn';
    else if (checkMarket('/fi'))
      marketToSet = 'fi';
    else if (checkMarket('/se'))
      marketToSet = 'se';
    else if (checkMarket('/gb'))
      marketToSet = 'gb';
    else if (checkMarket('/int'))
      marketToSet = 'int';
    else if (checkMarket('/en'))
      marketToSet = 'int';
    else
      marketToSet = '';

    const lngCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('lng='))
      ?.split('=')[1];

    if (!marketToSet && lngCookie) {
      let pathWithoutMarket = location.pathname;
      marketToSet = lngCookie;

      if (pathWithoutMarket.startsWith(`/${currentMartket}`))
        pathWithoutMarket = location.pathname.substring(currentMartket.length + 1);


      if (!pathWithoutMarket.startsWith('/')) pathWithoutMarket = `/${pathWithoutMarket}`;
      navigate(`/${lngCookie}${pathWithoutMarket}`);

    }

    dispatch(setMarket(marketToSet));

    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
      const [name, value] = cookie.split('=').map(c => c.trim());
      acc[name] = value;
      return acc;
    }, {} as Record<string, string>);

    if (marketToSet != '' && cookies['laerdalsupport-market'] == marketToSet) {
      dispatch(setTranslateWithAi(cookies['laerdalsupport-translate-with-ai'] === 'true'));
      dispatch(setTranslateWithAiMarket(cookies['laerdalsupport-market'] ?? ''));
    }
    else {
      dispatch(setTranslateWithAi(false));
      dispatch(setTranslateWithAiMarket(''));
    }
    setInitialMarketSet(true);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    posthog.capture('$pageview')
    handlePageChange();
  }, [location.pathname]);

  useEffect(() => {
    window.addEventListener('beforeunload', handlePageChange);

    return () => {
      window.removeEventListener('beforeunload', handlePageChange);
    };
  }, []);

  useEffect(() => {
    const show = !location.pathname.startsWith('/embeded')
      && !location.pathname.startsWith(`/${currentMartket}/embeded`)
      && !location.pathname.startsWith('/simcapture')
      && !location.pathname.startsWith(`/${currentMartket}/simcapture`);

    setShowHeaderFooter(show);

  }, [currentMartket, location.pathname]);

  const configurePosthog = (userParam: User | undefined) => {
    try {
      //@ts-ignore
      if (hasOneTrustConsent()) {
        //posthog settings

        // @ts-ignore
        window.posthog?.set_config({ disable_surveys: false });

        if (!!userParam) {
          //@ts-ignore
          window.posthog?.identify?.(
            userParam.id,
            {
              org_role: userParam.currentOrganization?.members.find(member => member.user.id === userParam.id)?.permissions.findIndex(permission => permission.permissionId === 'account.owner') ? 'admin' : 'user',
              language: i18next?.language ?? 'en',
              user_profile: userParam.companyRoles?.map(role => role.name).join('/'),
              test_user: userParam.email.indexOf('@laerdal.') > -1
            }
          );
          // @ts-ignore
          window.posthog?.capture("User SignedIn");

          if (userParam.currentOrganization) {
            var industryString = ''
            try {
              industryString = Industry[userParam.currentOrganization.industry]
            } catch (error) {
              industryString = '';
            }

            //@ts-ignore
            window.posthog?.group?.(
              'organisation',
              userParam.currentOrganization.customerNo ?? userParam.currentOrganization.id,
              {
                orgId: userParam.currentOrganization.id,
                customerNumber: userParam.currentOrganization.customerNo,
                industry: industryString,
                country: userParam.currentOrganization.address.country.name
              });
          }
        }
      } else {
        // @ts-ignore
        window.posthog?.set_config({ disable_surveys: true });
      }
    } catch (error) {
      console.error('posthog config error', error);
    }
  }

  useEffect(() => {
    configurePosthog(user);
  }, [user]);

  setTimeout(() => {
    //@ts-ignore
    posthog.init(process.env.REACT_APP_POSTHOG_DBU, {
      //@ts-ignore
      api_host: window.OnetrustActiveGroups?.indexOf('C0002') >= 0 ?
        'https://anltcs.laerdal.com'
        : 'https://hogproxy.laerdal.com/ingest',
      ui_host: 'https://eu.posthog.com'
    });

    configurePosthog(user);
  }, 1000);

  const hasOneTrustConsent = () => {
    //@ts-ignore
    return window.OnetrustActiveGroups?.indexOf('C0002') >= 0
  };

  return (
    <>
      {loadingApp && <LoadingContainer><LoadingIndicator /></LoadingContainer>}
      {!loadingApp &&
        <React.Suspense fallback={<LoadingContainer><LoadingIndicator /></LoadingContainer>}>
          <ContentContainer>
            {showHeaderFooter &&
              <>
                <LanguageSelector />
                <Header />
              </>
            }

            <Routes>
              <Route path='/:market?/simcapture' element={<SimcapturePage />} ></Route>

              <Route path='/:market?/' element={<LandingPage />} />
              <Route path='/:market?/category/:categoryLink' element={<ProductCategoryPage />} />
              <Route path='/:market?/category/:categoryLink/product/:productLink' element={<ProductLinePage />} />
              <Route path='/:market?/category/:categoryLink/product/:productLink/component/:componentLink' element={<ProductLinePage />} />
              <Route path='/:market?/category/:categoryLink/product/:productLink/articles/:componentLink' element={<ProductLineArticlesPage />} />

              <Route path='/:market?/articles' element={<ArticleSearchPage />} />
              <Route path='/:market?/embeded/search' element={<EmbededSearchPage />} />
              <Route path='/:market?/embeded/articles/:articleNumber' element={<EmbededArticlePage />} />

              {process.env.REACT_APP_PART_FINDER_ENABLED?.toString() == 'true' &&
                <Route path='/:market?/partFinder/:id' element={<PartFinderItemPage />} />
              }
              {process.env.REACT_APP_PART_FINDER_ENABLED?.toString() == 'true' &&
                <Route path='/:market?/partFinder' element={<PartFinderSearchPage />} />
              }

              <Route path='/:market?/articles/:articleNumber' element={<ArticlePage />} />
              <Route path='/:market?/articles/Knowledge/:title' element={<ArticlePage />} />

              <Route path='/:market?/case/create' element={<CaseCreatePage />} />
              <Route path='/:market?/case/create/:option' element={<CaseSuboptionsPage />} />
              <Route path='/:market?/case/create/:option/:suboption' element={<CaseFormPage />} />
              <Route path='/:market?/case/create/confirmation/:caseNumber' element={<CaseConfirmationPage />} />
            </Routes>

            {showHeaderFooter
              &&
              <FooterContainer>
                <SiteFooter
                  id="helpu-footer"
                  toTopText={t('TOP', { ns: 'cases' })}
                  sitename='Support'
                  copyrightText={
                    t(`Copyright © {{year}} Laerdal Medical. All Rights Reserved.`,
                      { ns: 'landingPage', year: new Date().getFullYear() })
                  }
                  bottomLinks={[
                    {
                      label: t('Terms of Use', { ns: 'landingPage' }),
                      to: 'https://laerdal.com/support/customer-service/terms-of-use/',
                    },
                    {
                      label: t('Privacy Statement', { ns: 'landingPage' }),
                      to: 'https://laerdal.com/support/customer-service/privacy-statement/',
                    },
                    {
                      label: t('Accessibility statement', { ns: 'landingPage' }),
                      to: 'https://laerdal.com/support/customer-service/accessibility/',
                    },
                    {
                      label: t('Sustainability', { ns: 'landingPage' }),
                      to: 'https://laerdal.com/about-us/sustainability/',
                    }
                  ]}
                />
              </FooterContainer>
            }
          </ContentContainer>
        </React.Suspense>
      }
    </>
  );
}

export default App;